import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'

import { captureException } from 'utils/SentryHelpers'
import app from 'containers/app'

import { epic as attributesEpic } from './attribute'
import { epic as segmentsEpic } from './segment'
import { epic as timelineEpic } from './timeline'
import { api } from './api'
import * as selectors from './selectors'
import * as actions from './actions'
import * as OrgSelectors from 'app/modules/org/selectors'
import { parseEndUser } from './models'
import * as SocketActions from 'app/modules/socket/actions'
import * as SocketConstants from 'app/modules/socket/constants'
import * as GateSelectors from 'app/modules/gates/selectors'
import * as DemoConstants from 'app/modules/reports/demodata'
import * as ConversationActions from 'app/modules/conversation/actions'
import * as ConversationSelectors from 'app/modules/conversation/selectors'
import { List } from 'immutable'

const fetch = (action$, { getState }) => {
  return action$
    .ofType(actions.fetchEndUser.toString())
    .map(action => action.payload)
    .filter(({ id }) => !!id)
    .filter(({ id }) => !selectors.isFetched(getState(), { id }))
    .mergeMap(({ id }) => {
      if (GateSelectors.hasDemoOrg(getState())) {
        const data = parseEndUser(DemoConstants.makeFakePerson(id))
        return Observable.of(actions.receiveEndUser(data))
      } else {
        return api
          .fetch(id)
          .map(actions.receiveEndUser)
          .startWith(actions.fetchEndUserPending({ id }))
          .takeUntil(
            action$
              .ofType(actions.fetchEndUserCancelled.toString())
              .filter(action => action.payload.id === id)
          )
          .catch(error => {
            captureException(error)
            return Observable.of(actions.fetchEndUserFailed({ id, error }))
          })
      }
    })
}

const updateEndUser = action$ => {
  return action$
    .ofType(actions.updateEndUser.toString())
    .pluck('payload')
    .mergeMap(({ endUserId, endUserParams }) => {
      return api
        .updateEndUser(endUserId, endUserParams)
        .map(actions.receiveEndUser)
        .startWith(actions.updateEndUserPending)
        .catch(error => {
          captureException(error)
          return Observable.of(actions.updateEndUserFailed({ endUserId, error }))
        })
    })
}

const bulkFetch = (action$, { getState }) => {
  return action$
    .ofType(actions.fetchEndUserList.toString())
    .map(action => action.payload.ids.filter(id => !selectors.isFetched(getState(), { id })))
    .filter(ids => ids.length > 0)
    .mergeMap(ids => {
      if (GateSelectors.hasDemoOrg(getState())) {
        const data = List(DemoConstants.makeFakePeopleWithIds(ids.length, ids)).map(parseEndUser)
        return Observable.of(actions.fetchEndUserListSuccess({ ids, data }))
      } else {
        return api
          .bulkFetch(ids)
          .map(data => actions.fetchEndUserListSuccess({ ids, data }))
          .startWith(actions.fetchEndUserListPending({ ids }))
          .catch(error => {
            captureException(error)
            return Observable.of(actions.fetchEndUserListFailed({ ids }))
          })
      }
    })
}

const fetchFollowing = action$ => {
  return action$.ofType(actions.fetchFollowing.toString()).mergeMap(action => {
    const { id } = action.payload
    return api
      .fetchFollowing(id)
      .map(({ following }) => actions.fetchFollowingSuccess({ id, following }))
      .startWith(actions.fetchFollowingPending({ id }))
      .catch(error => {
        captureException(error)
        return Observable.of(actions.fetchFollowingFailed({ id, error }))
      })
  })
}

const updateFollowing = action$ => {
  return action$.ofType(actions.updateFollowing.toString()).mergeMap(action => {
    const { id, following } = action.payload
    return api
      .updateFollowing(id, following)
      .map(({ following }) => actions.updateFollowingSuccess({ following, id }))
      .startWith(actions.updateFollowingPending({ id }))
      .catch(error => {
        captureException(error)
        return Observable.of(actions.updateFollowingFailed({ id, error }))
      })
  })
}

const pushEndUser = (action$, store) => {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter(payload => payload.clusterName === SocketConstants.SocketCluster.PUSH)
    .filter(payload => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable.create(observer => {
        channel.on('end-user-updated', payload => {
          const state = store.getState()
          const { id } = payload
          if (selectors.isFetched(state, { id })) {
            app.actions.EndUserActions.setEndUserFromPusher(payload)
            observer.next(actions.receiveEndUser(parseEndUser(payload)))
          }
        })
      }).takeUntil(leaveChannel$)
    })
}

const pushEndUserForSplitChannels = (action$, store) => {
  return action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter(payload => payload.clusterName === SocketConstants.SocketCluster.PUSH)
    .filter((payload) => _.includes(payload.channelName, 'subscribe-end-user'))
    .mergeMap(({ channel, leaveChannel$ }) => {
      return Observable.create(observer => {
        channel.on('end-user-fetched', payload => {
          app.actions.EndUserActions.setEndUserFromPusher(payload)
          observer.next(actions.receiveEndUser(parseEndUser(payload)))
        })
        channel.on('end-user-updated', payload => {
          app.actions.EndUserActions.setEndUserFromPusher(payload)
          observer.next(actions.receiveEndUser(parseEndUser(payload)))
        })
        return Observable.empty()
      }).takeUntil(leaveChannel$)
    })
}

const updateCQL = action$ => {
  return action$
    .ofType(actions.updateCQL.toString())
    .pluck('payload')
    .mergeMap(({ conversationId, endUserId, score }) => {
      return api
        .updateCQL(endUserId, score, conversationId)
        .map(actions.updateCQLSuccess)
        .startWith(actions.updateCQLPending())
        .catch(error => {
          captureException(error)
          return Observable.of(actions.updateCQLFailed(error))
        })
    })
}

const checkUnsubscribed = (action$, store) => {
  return action$
    .ofType(actions.checkUnsubscribed.toString())
    .pluck('payload')
    .switchMap(({ email }) => {
      const orgId = OrgSelectors.orgId(store.getState())
      return api
        .checkUnsubscribed(email, orgId)
        .map(actions.checkUnsubscribedSuccess)
        .startWith(actions.checkUnsubscribedPending())
        .catch(error => {
          captureException(error)
          return Observable.of(actions.checkUnsubscribedFailed(error))
        })
    })
}

const fetchUnsubscribedCount = action$ => {
  return action$.ofType(actions.fetchUnsubscribedCount.toString()).switchMap(() => {
    return api
      .fetchUnsubscribedCount()
      .map(actions.fetchUnsubscribedCountSuccess)
      .startWith(actions.fetchUnsubscribedCountPending())
      .catch(() => {
        return Observable.of(actions.fetchUnsubscribedCountFailed())
      })
  })
}

const unsubscribeById = action$ => {
  return action$
    .ofType(actions.unsubscribeById.toString())
    .pluck('payload')
    .mergeMap(({ endUserId, reason }) =>
      api
        .unsubscribeById({ endUserId, reason })
        .map(actions.unsubscribeSuccess)
        .startWith(actions.unsubscribePending())
        .catch(error => {
          captureException(error, { extra: { endUserId }})
          return Observable.of(actions.unsubscribeFailed(error))
        })
  )
}

const unsubscribeByEncodedEmail = action$ => {
  return action$
    .ofType(actions.unsubscribeByEncodedEmail.toString())
    .pluck('payload')
    .mergeMap(({ email, endUserId, reason }) =>
      api
        .unsubscribeByEncodedEmail({ email, endUserId, reason })
        .map(actions.unsubscribeSuccess)
        .startWith(actions.unsubscribePending())
        .catch(error => {
          captureException(error, { extra: { endUserId }})
          return Observable.of(actions.unsubscribeFailed(error))
        })
  )
}


const unsubscribeByEmailAndOrgId = action$ => {
  return action$
    .ofType(actions.unsubscribeByEmailAndOrgId.toString())
    .pluck('payload')
    .mergeMap(({ email, orgId }) =>
      api
        .unsubscribeByEmailAndOrgId({ email, orgId })
        .map(actions.unsubscribeSuccess)
        .startWith(actions.unsubscribePending())
        .catch(error => {
          captureException(error, { extra: { email, orgId }})
          return Observable.of(actions.unsubscribeFailed(error))
        })
  )
}

const resubscribe = action$ => {
  return action$
    .ofType(actions.resubscribe.toString())
    .pluck('payload')
    .mergeMap(({ email, endUserId }) => {
      return api
        .resubscribe({ email, endUserId })
        .map(actions.resubscribeSuccess)
        .startWith(actions.resubscribePending())
        .catch(error => {
          return Observable.of(actions.resubscribeFailed(error))
        })
  })
}

const resubscribeByEmailandOrgId = action$ => {
  return action$
    .ofType(actions.resubscribeByEmailandOrgId.toString())
    .pluck('payload')
    .mergeMap(({ email, orgId }) => {
      return api
        .resubscribeByEmailandOrgId({ email, orgId })
        .map(actions.resubscribeSuccess)
        .startWith(actions.resubscribePending())
        .catch(error => {
          return Observable.of(actions.resubscribeFailed(error))
        })
  })
}


const requestUnsubDeleteData = action$ =>
  action$
    .ofType(actions.requestUnsubDeleteData.toString())
    .pluck('payload')
    .mergeMap(({ email, orgId, endUserId }) =>
      api
        .requestUnsubDeleteData({ email, orgId, endUserId })
        .map(actions.requestUnsubDeleteDataSuccess)
        .startWith(actions.requestUnsubDeleteDataPending())
        .catch(err => Observable.of(actions.requestUnsubDeleteDataFailed(err)))
    )

const unsubscribe = action$ => {
  return action$
    .ofType(actions.unsubscribe.toString())
    .pluck('payload')
    .mergeMap(({ email }) => {
      return api
        .unsubscribe(email)
        .map(actions.unsubscribeSuccess)
        .startWith(actions.unsubscribePending())
        .catch(error => {
          captureException(error)
          return Observable.of(actions.unsubscribeFailed(error))
        })
    })
}

const createPropertyDefinition = action$ => {
  return action$
    .ofType(actions.createPropertyDefinition.toString())
    .pluck('payload')
    .mergeMap(({ name }) => {
      return (
        api
          // TODO: should we always create them of type STRING?
          .createNewContactAttribute([{ type: 'STRING', name, displayName: name }])
          .map(() => actions.createPropertyDefinitionSuccess(name))
          .startWith(actions.createPropertyDefinitionPending(name))
          .catch(error => {
            captureException(error)
            return Observable.of(actions.createPropertyDefinitionFailed({ error, name }))
          })
      )
    })
}

const requestContactData = action$ => {
  return action$
    .ofType(actions.requestContactData.toString())
    .pluck('payload')
    .mergeMap(payload => {
      return api
        .requestContactData(payload)
        .map(resp => {
          app.actions.NotificationActions.pushNotification({
            content:
              'Your data request was successfully received! You’ll receive an email with the info soon!',
          })
          return actions.requestContactDataSuccess(resp)
        })
        .startWith(actions.requestContactDataPending(payload))
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            variant: 'red',
            sticky: true,
            content: `We were unable to process your request. Please chat in and let us know so we can make sure you're taken care of.`,
          })
          captureException(error)
          return Observable.of(actions.requestContactDataFailed({ error, payload }))
        })
    })
}

const requestDeleteContactData = action$ => {
  return action$
    .ofType(actions.requestDeleteContactData.toString())
    .pluck('payload')
    .mergeMap(payload => {
      return api
        .requestDataDeletion(payload)
        .map(resp => {
          app.actions.NotificationActions.pushNotification({
            content:
              'Your data deletion request was successfully received and is being processed!',
          })
          return actions.requestDeleteContactDataSuccess(resp)
        })
        .startWith(actions.requestDeleteContactDataPending(payload))
        .catch(error => {
          app.actions.NotificationActions.pushNotification({
            variant: 'red',
            sticky: true,
            content: `We were unable to process your request. Please chat in and let us know so we can make sure you're taken care of.`,
          })
          captureException(error)
          return Observable.of(actions.requestDeleteContactDataFailed({ error, payload }))
        })
    })
}


const joinSpecificEndUserChannels = (action$, { getState }) => {
  return action$
    .ofType(actions.joinEndUserChannel.toString())
    .pluck('payload')
    .map((endUserId) => SocketActions.joinChannel({
      clusterName: SocketConstants.SocketCluster.PUSH,
      channelName: `subscribe-end-user:${OrgSelectors.orgId(getState())}-${endUserId}`,
    }))
}

const closeSpecficEndUserChannels = (action$, { getState }) => {
  return action$
    .ofType(actions.closeEndUserChannel.toString())
    .pluck('payload')
    .map((endUserId) => {
      return SocketActions.leaveChannel({
        clusterName: SocketConstants.SocketCluster.PUSH,
        channelName: `subscribe-end-user:${OrgSelectors.orgId(getState())}-${endUserId}`,
      })
    })
}

const updateSpecificEndUserChannels = (action$, { getState }) => {
  return action$
    .ofType(
      ConversationActions.fetchConversationListSuccess.toString(),
      ConversationActions.fetchNextConversationListSuccess.toString(),
      ConversationActions.receiveConversation.toString(),
      ConversationActions.setActiveConversation.toString(),
      ConversationActions.unsetActiveConversation.toString(),
    ) // any actions that could change the conversation list
    .mergeMap(() => {
      const state = getState()
      const query = ConversationSelectors.activeQuery(state)
      const activeEndUserIdsFromConversation = ConversationSelectors.endUserIdsOfActiveConversations(state, { query })
      const activeEndUserChannels = selectors.activeEndUserChannels(state)

      const newChannelsToJoin = activeEndUserIdsFromConversation.subtract(activeEndUserChannels)
      const channelsToLeave = activeEndUserChannels.subtract(activeEndUserIdsFromConversation)

      const actionsToDispatch = newChannelsToJoin.toList().map(endUserId => actions.joinEndUserChannel(endUserId))
        .concat(channelsToLeave.toList().map(endUserId => actions.closeEndUserChannel(endUserId)))
      return actionsToDispatch
    })
}

export const epic = combineEpics(
  fetch,
  updateEndUser,
  bulkFetch,
  fetchFollowing,
  updateFollowing,
  attributesEpic,
  segmentsEpic,
  timelineEpic,
  pushEndUser,
  pushEndUserForSplitChannels,
  updateCQL,
  checkUnsubscribed,
  unsubscribe,
  resubscribe,
  fetchUnsubscribedCount,
  createPropertyDefinition,
  requestContactData,
  requestDeleteContactData,
  unsubscribeById,
  unsubscribeByEncodedEmail,
  unsubscribeByEmailAndOrgId,
  requestUnsubDeleteData,
  joinSpecificEndUserChannels,
  closeSpecficEndUserChannels,
  updateSpecificEndUserChannels,
  resubscribeByEmailandOrgId
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/endUser/epic.js